import * as React from "react";
import { format } from "date-fns";

import { ConnectProto } from "@racoon/api";
import { Namespace, useDateFnsLocale, useT } from "@racoon/i18n";
import { fromTimestamp, getEnumName } from "@racoon/lib";

import { Hyperlink, PlateNumber } from "@racoon/ui";
import { useJobCard } from "~/pages/dashboard/orders/ui/use-job-card";
import { fullName } from "~/shared/helpers";
import { useFreshJob } from "~/shared/hooks/use-fresh-job";
import { SectionCaption } from "~/shared/ui/section-caption";
import { containerLabel, plannedVolume } from "../model/container-volume";
import { outcomeOf } from "../model/outcome-correction";
import { ManualChangeBlock } from "./manual-change.block";

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  // Each row sizes its own label, so values stay on one line
  <div className="flex items-baseline justify-between gap-2 text-xs leading-5">
    <dt className="shrink-0 text-text-subtle">{label}</dt>
    <dd className="min-w-0 text-right text-text-body">{children}</dd>
  </div>
);

export const MswReportInfo: React.FC<{
  job: ConnectProto.Job;
  /** The job's route; its shift stands in for a standalone job's (e.g. a map photo pin). */
  route?: ConnectProto.Route;
  onOpenRoute: (route: ConnectProto.Route) => void;
  statusSelect: React.ReactNode;
  incidentSelect: React.ReactNode;
}> = ({ job, route, onOpenRoute, statusSelect, incidentSelect }) => {
  const t = useT();
  const dateLocale = useDateFnsLocale();
  const { openJob } = useJobCard();
  // Only GetJobById says who saved the job last
  const freshJob = useFreshJob(job);

  const shift = job.driverOnShift ?? route?.driverOnShift;
  const truck = shift?.truck;
  const truckTypeName = truck ? getEnumName(ConnectProto.TruckTypeSchema, truck.type) : undefined;
  const jobTypeName = getEnumName(ConnectProto.JobTypeSchema, job.type);
  const container = containerLabel(job);
  const planned = plannedVolume(job);
  const doneAt = fromTimestamp(job.factEndAt) ?? fromTimestamp(job.planEndAt);
  // «Невывоз: …», «Выполнено: …»
  const outcomeName = getEnumName(ConnectProto.JobStatusSchema, outcomeOf(job.status));
  const doneAtLabel = outcomeName
    ? `${t(Namespace.Enum, outcomeName)}:`
    : t(Namespace.PhotoReport, "details.time");
  const crew = (shift?.crew ?? []).filter((member) => member.id !== shift?.driverId);
  const routeNumber = job.routeId ? `M${String(job.routeId)}` : undefined;

  return (
    <div className="flex w-[240px] shrink-0 flex-col overflow-y-auto border-r border-border-primary">
      <div className="flex flex-col gap-4 p-6">
        <SectionCaption>{t(Namespace.PhotoReport, "crew")}</SectionCaption>
        <div className="flex flex-col items-start gap-4">
          <div className="flex flex-col gap-2 text-sm leading-4 text-text-body">
            {shift?.driver && <span>{fullName(shift.driver)}</span>}
            {crew.map((member) => (
              <span key={String(member.id)}>{fullName(member)}</span>
            ))}
          </div>
          {truck?.plateNumber && <PlateNumber plateNumber={truck.plateNumber} size="lg" />}
        </div>
        <div className="flex flex-col gap-2 text-sm leading-4 text-text-subtle">
          {truck?.model && <span>{truck.model}</span>}
          {truckTypeName && <span>{t(Namespace.Enum, truckTypeName)}</span>}
          {job.serviceProvider?.name && <span>{job.serviceProvider.name}</span>}
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-4 p-6">
        <SectionCaption>{t(Namespace.PhotoReport, "jobDetails")}</SectionCaption>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            {statusSelect}
            {incidentSelect}
            {freshJob && <ManualChangeBlock job={freshJob} />}
          </div>
          <dl className="flex flex-col gap-2">
            {routeNumber ? (
              <DetailRow label={t(Namespace.PhotoReport, "details.routeId")}>
                {route ? (
                  <Hyperlink
                    onOpen={() => onOpenRoute(route)}
                    className="leading-4"
                    data-testid="report-route-link"
                  >
                    {routeNumber}
                  </Hyperlink>
                ) : (
                  <span>{routeNumber}</span>
                )}
              </DetailRow>
            ) : null}
            <DetailRow label={t(Namespace.PhotoReport, "details.jobId")}>
              <Hyperlink
                onOpen={() => openJob(job)}
                className="leading-4"
                data-testid="report-job-link"
              >
                Z{String(job.id)}
              </Hyperlink>
            </DetailRow>
            {doneAt && (
              <DetailRow label={doneAtLabel}>
                <span className="inline-flex gap-2">
                  <span>{format(doneAt, "d MMM yyyy", { locale: dateLocale })}</span>
                  <span>{format(doneAt, "HH:mm")}</span>
                </span>
              </DetailRow>
            )}
            {jobTypeName && (
              <DetailRow label={t(Namespace.PhotoReport, "details.jobType")}>
                {t(Namespace.Enum, jobTypeName)}
              </DetailRow>
            )}
            {container && (
              <DetailRow label={t(Namespace.PhotoReport, "details.containerGroup")}>
                {container}
              </DetailRow>
            )}
            {planned !== undefined && (
              <DetailRow label={t(Namespace.PhotoReport, "details.nominalVolume")}>
                {planned} {t(Namespace.Settings, "locationSettings.cubicMeters")}
              </DetailRow>
            )}
          </dl>
        </div>
      </div>
    </div>
  );
};
